/**
 * WoZ-specific flow enums & types.
 * Generic flow types are re-exported from the shared chatbot package.
 */

import type { FlowContext } from '@footsteps/shared';
import type { WozCaseQuestion } from '../woz-case.types';

/** Identifiers for every flow step. */
export enum FlowStepId {
  FindAddress = 'find-address',
  FetchAddress = 'fetch-address',
  AddressUnavailable = 'address-unavailable',
  AddressFetchError = 'address-fetch-error',
  AddressInfo = 'address-info',
  DescribeComplaints = 'describe-complaints',
  ClassifyComplaints = 'classify-complaints',
  ClassifyComplaintsRetry = 'classify-complaints-retry',
  ClassificationError = 'classification-error',
  ClassificationFailed = 'classification-failed',
  ClassificationNoRelevant = 'classification-no-relevant',
  NoRelevantRetryOrFinish = 'no-relevant-retry-or-finish',
  DescribeComplaintsRetry = 'describe-complaints-retry',
  ConfirmSingleComplaint = 'confirm-single-complaint',
  ChooseComplaintMode = 'choose-complaint-mode',
  GetFlow = 'get-flow',
  GetFlowError = 'get-flow-error',
  /** Dynamic intro message shown before a complaint's questions. */
  GetFlowIntro = 'get-flow-intro',
  /** Dynamic question step (single- or multiple-select). */
  GetFlowQuestion = 'get-flow-question',
  AskMoreComplaints = 'ask-more-complaints',
  FlowComplete = 'flow-complete',
}

/** Identifiers for flow action buttons. */
export enum FlowActionId {
  FindAddress = 'find-address',
  MultiComplaint = 'multi-complaint',
  SingleComplaint = 'single-complaint',
  MoreComplaintsYes = 'more-complaints-yes',
  MoreComplaintsNo = 'more-complaints-no',
  NoRelevantRetry = 'no-relevant-retry',
  NoRelevantFinish = 'no-relevant-finish',
}

/** Minimal interface exposed to step-meta callbacks. */
export type WozCaseViewModelRef = {
  completeTrigger(triggerId: string, data?: FlowContext): void;
  setQuestionAnswer(complaintIdx: number, questionIdx: number, answer: WozCaseQuestion['answer']): void;
};

/** Callbacks that make action steps self-describing. */
export type ActionStepCallbacks = {
  onComplete: (viewModel: WozCaseViewModelRef, data: unknown) => void;
  formatBubble: (data: unknown) => string | null;
};

// Re-export all generic flow types from the shared package
export type {
  FlowContext,
  FlowActionDefinition,
  FlowMultipleSelectOption,
  FlowAsyncResult,
  FlowAsyncHandler,
  FlowSkipCondition,
  FlowStepMeta,
  FlowStepRef,
  FlowStepDefinition,
  FlowState,
  FlowHandlerRegistry,
  FlowSkipIfRegistry,
} from '@footsteps/shared';
